import { useEffect, useState } from 'preact/hooks';
import type { PlaylistDetailDto } from '../../shared/types';
import { api, ApiError } from '../api';
import { EpisodeRow } from '../components/EpisodeRow';
import { Icon } from '../components/Icon';
import { SkeletonEpisodeList } from '../components/Skeleton';
import { navigate } from '../router';
import { playEpisode, upNext } from '../state/player';
import { isSignedIn, sessionLoading } from '../state/session';

export function Playlist({ id }: { id: string }) {
  const playlistId = Number.parseInt(id, 10);
  const [data, setData] = useState<PlaylistDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadPlaylist = () => {
    if (!isSignedIn.value) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .playlist(playlistId)
      .then((res) => {
        setData(res);
        setEditName(res.playlist.name);
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Could not load playlist.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (sessionLoading.value) return;
    loadPlaylist();
  }, [playlistId, sessionLoading.value]);

  const handleRename = async (e: Event) => {
    e.preventDefault();
    const name = editName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.renamePlaylist(playlistId, name);
      if (data) {
        setData({
          ...data,
          playlist: { ...data.playlist, name },
        });
      }
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to rename playlist.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this playlist?')) return;
    setDeleting(true);
    try {
      await api.deletePlaylist(playlistId);
      navigate('/library');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete playlist.');
      setDeleting(false);
    }
  };

  const handleRemoveItem = async (episodeId: number) => {
    try {
      await api.removeFromPlaylist(playlistId, episodeId);
      if (data) {
        setData({
          ...data,
          items: data.items.filter((item) => item.episode.id !== episodeId),
        });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to remove episode from playlist.');
    }
  };

  const handlePlayAll = () => {
    if (!data || data.items.length === 0) return;
    const [first, ...rest] = data.items.map((i) => i.episode);
    if (first) {
      upNext.value = rest;
      void playEpisode(first);
    }
  };

  if (sessionLoading.value || loading) {
    return (
      <div class="view">
        <SkeletonEpisodeList count={4} label="Loading playlist" />
      </div>
    );
  }

  if (!isSignedIn.value) {
    return (
      <div class="view">
        <h1>Playlist</h1>
        <p class="muted">Sign in to view your playlists.</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div class="view">
        <header class="view__header">
          <a class="button button--ghost" href="/library">
            ← Back to Library
          </a>
        </header>
        <div class="error" style={{ margin: '1rem 0' }}>
          <p>{error || 'Playlist not found'}</p>
          <button type="button" class="button button--ghost" onClick={loadPlaylist}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { playlist, items } = data;

  return (
    <div class="view">
      <header class="view__header" style={{ marginBottom: '1rem' }}>
        <div class="stack stack--row" style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <a class="button button--ghost" href="/library">
            ← Back to Library
          </a>
          <div class="stack stack--row" style={{ gap: '0.5rem' }}>
            {!editing && (
              <button
                type="button"
                class="button button--ghost"
                onClick={() => {
                  setEditName(playlist.name);
                  setEditing(true);
                }}
              >
                Rename
              </button>
            )}
            <button
              type="button"
              class="button button--ghost"
              disabled={deleting}
              onClick={handleDelete}
              style={{ color: 'var(--warn)' }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>

        {editing ? (
          <form onSubmit={handleRename} class="stack stack--row" style={{ gap: '0.5rem', marginTop: '0.5rem' }}>
            <input
              type="text"
              class="input"
              value={editName}
              onInput={(e) => setEditName((e.target as HTMLInputElement).value)}
              placeholder="Playlist name"
              maxLength={120}
              required
              autoFocus
            />
            <button type="submit" class="button button--primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              class="button button--ghost"
              disabled={saving}
              onClick={() => setEditing(false)}
            >
              Cancel
            </button>
          </form>
        ) : (
          <h1 style={{ marginTop: '0.5rem' }}>{playlist.name}</h1>
        )}

        <p class="muted">
          {items.length} {items.length === 1 ? 'episode' : 'episodes'}
        </p>

        {items.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <button type="button" class="button button--primary" onClick={handlePlayAll}>
              <Icon name="play" size={16} /> Play all
            </button>
          </div>
        )}
      </header>

      {items.length === 0 ? (
        <div class="empty">
          <h2>Playlist is empty</h2>
          <p class="muted">Add episodes to this playlist from any podcast or episode page.</p>
          <div style={{ marginTop: '1rem' }}>
            <a class="button button--ghost" href="/search">
              Browse podcasts
            </a>
          </div>
        </div>
      ) : (
        <div class="episode-list">
          {items.map(({ episode }) => (
            <div key={episode.id} class="playlist-item-row" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ flex: 1 }}>
                <EpisodeRow episode={episode} showArtwork={true} showPodcast={true} />
              </div>
              <button
                type="button"
                class="icon-button"
                title="Remove from playlist"
                aria-label={`Remove ${episode.title} from playlist`}
                onClick={() => handleRemoveItem(episode.id)}
              >
                <Icon name="x" size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
